import { SignOutBlockedError } from '@/features/auth/sign-out-blocked-error';
import { waitForWorkspaceOperation } from '@/shared/api/wait-for-workspace-operation';

import { workspaceFailureMessage } from '../workspace-feedback';

import type { CurrentWorkspace, WorkspaceOperation } from '@mio/shared';

/** Finish a verified checkpoint while the page keeps editor/explanation input locked. */
export async function prepareWorkshopSignOut(input: {
  workspace: CurrentWorkspace['workspace'];
  latest: CurrentWorkspace;
  initialized: boolean;
  busy: boolean;
  /** A periodic disk backup owns the workspace; sign-out waits for it instead of failing. */
  backgroundSaving?: boolean;
  awaitBackground?: () => Promise<unknown>;
  otherUnsavedDrafts: boolean;
  flush: () => Promise<unknown>;
  checkpoint: () => Promise<WorkspaceOperation>;
}) {
  const latest = input.latest.workspace;
  // Only live compute has editor buffers to certify. Paused, failed or
  // unconfirmed compute keeps its last checkpoint; the draft is still flushed.
  const needsCheckpoint = latest?.activeAttemptId && latest.state === 'ready';
  if (
    needsCheckpoint &&
    (!input.initialized ||
      input.workspace?.id !== latest.id ||
      input.workspace.generation !== latest.generation ||
      input.workspace.activeAttemptId !== latest.activeAttemptId)
  )
    throw new SignOutBlockedError(
      'Workshop is still loading or its activity changed. Open Workshop, wait for it to connect, then sign out again.',
    );
  if (input.busy) {
    // Any running operation (a periodic backup or the learner's own save,
    // switch or submission) settles within a bounded time; wait for it rather
    // than bouncing the learner back to the page.
    if (!input.awaitBackground)
      throw new SignOutBlockedError(
        'A workspace operation is still finishing. Wait for it, then sign out again.',
      );
    try {
      await input.awaitBackground();
    } catch {
      throw new SignOutBlockedError(
        input.backgroundSaving
          ? 'An automatic backup is still running. Wait for it to finish, then sign out again.'
          : 'A workspace operation is still finishing. Wait for it, then sign out again.',
      );
    }
  }
  if (input.otherUnsavedDrafts)
    throw new SignOutBlockedError(
      'An explanation in another activity is unsaved. Return to that activity and save it before signing out.',
    );
  try {
    await input.flush();
  } catch {
    throw new SignOutBlockedError(
      'Your explanation could not be saved. Resolve the save error in Workshop before signing out.',
    );
  }
  if (!needsCheckpoint) return;
  try {
    const operation = await waitForWorkspaceOperation(await input.checkpoint());
    if (operation.status === 'failed')
      throw new SignOutBlockedError(
        `${workspaceFailureMessage(operation.errorCode)} You are still signed in.`,
      );
  } catch (error) {
    if (error instanceof SignOutBlockedError) throw error;
    throw new SignOutBlockedError(
      'Could not confirm your code was backed up. Check the operation in Workshop before trying to sign out again.',
    );
  }
}
